import {
  IDropdownStyleProps,
  IDropdownStyles
} from '@fluentui/react/lib/Dropdown';
import { ITheme } from '@fluentui/react/lib/Styling';
import { ContrastColors, CoreColors, DarkColors } from '../teams-colors';
import { teamsBorderRadius, teamsButtonBorderRadius } from './constants';

export function getDropdownStyles(
  themeName: string,
  _theme: ITheme,
  background?: string
) {
  return (props: IDropdownStyleProps): Partial<IDropdownStyles> => {
    const { theme, disabled, isOpen, hasError, isRenderingPlaceholder } = props;
    if (!theme) {
      throw new Error(
        'theme is undefined or null in base Dropdown getStyles function.'
      );
    }
    const { palette } = theme;

    let ROOT_BACKGROUND_COLOR = theme.palette.neutralLight;
    let FIELD_GROUP_OPACITY = 0.36;

    let HOVER_COLOR = theme.palette.black;
    let HOVER_BACKGROUND_COLOR = theme.palette.neutralLight;
    let FOCUS_BACKGROUND_COLOR = theme.palette.neutralQuaternaryAlt;

    let FIELD_GROUP_BORDER_WIDTH = '0 0 2px 0';
    let FIELD_GROUP_BORDER_COLOR = 'transparent';
    let CALLOUT_BORDER_COLOR = CoreColors.appBlackAlpha10;

    let FIELD_GROUP_BORDER_WIDTH_ACTIVE = FIELD_GROUP_BORDER_WIDTH;
    let FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE = theme.palette.themePrimary;

    let FIELD_GROUP_BORDER_COLOR_DISABLED = 'transparent';
    let FIELD_COLOR_PLACEHOLDER_DISABLED = theme.palette.neutralDark;

    let ERROR_BORDER_COLOR = CoreColors.appRed;

    if (themeName === 'dark') {
      ROOT_BACKGROUND_COLOR = theme.palette.white;
      CALLOUT_BORDER_COLOR = palette.white;

      HOVER_COLOR = theme.palette.black;
      HOVER_BACKGROUND_COLOR = theme.palette.neutralQuaternary;
      FOCUS_BACKGROUND_COLOR = theme.palette.neutralTertiaryAlt;

      FIELD_COLOR_PLACEHOLDER_DISABLED = theme.palette.neutralPrimary;

      ERROR_BORDER_COLOR = DarkColors.appRed;
    }
    if (themeName === 'contrast') {
      ROOT_BACKGROUND_COLOR = 'inherit';
      FIELD_GROUP_OPACITY = 1;

      HOVER_COLOR = 'inherit';
      HOVER_BACKGROUND_COLOR = theme.palette.themeDark;
      FOCUS_BACKGROUND_COLOR = theme.palette.themeDark;

      FIELD_GROUP_BORDER_COLOR = theme.palette.black;
      FIELD_GROUP_BORDER_WIDTH = '1px 1px 1px 1px';
      CALLOUT_BORDER_COLOR = palette.black;

      FIELD_GROUP_BORDER_WIDTH_ACTIVE = '1px 1px 2px 1px';
      FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE = theme.palette.themeDark;

      FIELD_GROUP_BORDER_COLOR_DISABLED = ContrastColors.appGreenContrast;
      FIELD_COLOR_PLACEHOLDER_DISABLED = ContrastColors.appGreenContrast;

      ERROR_BORDER_COLOR = theme.palette.black;
    }
    // If this combobox is on gray background
    if (background === 'gray') {
      ROOT_BACKGROUND_COLOR = theme.palette.white;
    }

    const isActive = {
      borderWidth: FIELD_GROUP_BORDER_WIDTH_ACTIVE,
      borderColor: FIELD_GROUP_BORDER_COLOR,
      borderBottomColor: FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE,
      borderBottomRightRadius: teamsButtonBorderRadius,
      borderBottomLeftRadius: teamsButtonBorderRadius
    };

    const TEXT_COLOR =
      themeName === 'contrast' ? 'inherit' : theme.palette.black;

    const commonStyles = {
      selectors: {
        '.ms-Dropdown-titleIsPlaceHolder': {
          color: theme.palette.black
        },
        '.ms-Dropdown-title': {
          color: TEXT_COLOR
        },
        '.ms-Dropdown-caretDown': {
          color: theme.palette.black
        }
      }
    };

    const styles = {
      callout: {
        border: `1px solid ${CALLOUT_BORDER_COLOR}`,
        selectors: {
          '.ms-Callout-main': {
            paddingTop: '8px',
            paddingBottom: '8px',
            maxHeight: `21.1428rem !important` // from @fluentui/react/src/themes/teams/components/Dropdown/dropdownVariables.ts
          }
        }
      },
      title: [
        {
          paddingLeft: '12px',
          color: TEXT_COLOR,
          minWidth: 'auto',
          border: 'none',
          height: 30,
          background: 'transparent'
        },
        isRenderingPlaceholder && {
          color: TEXT_COLOR
        },
        disabled && {
          color: FIELD_COLOR_PLACEHOLDER_DISABLED
        }
      ],
      dropdownItemHeader: {
        padding: '0 8px',
        color: theme.palette.neutralDark
      },
      dropdownDivider: {
        margin: '4px 0',
        height: '2px',
        backgroundColor: theme.palette.neutralLight,
        border: 'none'
      },
      dropdown: [
        {
          borderRadius: teamsBorderRadius,
          borderWidth: FIELD_GROUP_BORDER_WIDTH,
          borderStyle: 'solid',
          borderColor: FIELD_GROUP_BORDER_COLOR,
          background: ROOT_BACKGROUND_COLOR,
          height: 32,
          boxSizing: 'border-box',
          color: theme.palette.black
        },
        !disabled && {
          selectors: {
            ':hover': {
              ...commonStyles
            },
            ':active': {
              ...commonStyles
            },
            ':focus': {
              ...commonStyles
            },
            ':focus::after': {
              border: 'none'
            }
          }
        },
        isOpen &&
          !disabled &&
          !hasError && {
            ...isActive
          },
        !disabled &&
          !hasError && {
            selectors: {
              '.ms-Fabric--isFocusVisible &': {
                selectors: {
                  ':focus': {
                    ...isActive
                  }
                }
              }
            }
          },
        disabled && {
          opacity: FIELD_GROUP_OPACITY,
          borderColor: FIELD_GROUP_BORDER_COLOR_DISABLED
        },
        hasError && {
          borderColor: ERROR_BORDER_COLOR,
          borderBottomRightRadius: teamsButtonBorderRadius,
          borderBottomLeftRadius: teamsButtonBorderRadius,
          borderWidth: FIELD_GROUP_BORDER_WIDTH
        }
      ],
      caretDownWrapper: {
        height: 30
      },
      caretDown: [
        {
          color: theme.palette.black
        },
        disabled && {
          color: FIELD_COLOR_PLACEHOLDER_DISABLED
        }
      ],
      dropdownItemDisabled: {
        color: FIELD_COLOR_PLACEHOLDER_DISABLED,
        opacity: FIELD_GROUP_OPACITY
      },
      dropdownItems: {
        selectors: {
          '.ms-Checkbox': {
            selectors: {
              '& .is-checked': {
                background: 'transparent'
              },
              ':hover': {
                background: HOVER_BACKGROUND_COLOR
              }
            }
          }
        }
      },
      dropdownItem: [
        {
          color: theme.palette.black,
          selectors: {
            ':hover': {
              color: HOVER_COLOR,
              background: HOVER_BACKGROUND_COLOR
            },
            ':focus': {
              color: HOVER_COLOR,
              background: HOVER_BACKGROUND_COLOR
            },
            ':active': {
              color: HOVER_COLOR,
              background: FOCUS_BACKGROUND_COLOR
            },
            ':focus::after': {
              display: 'none'
            },
            '.ms-Fabric--isFocusVisible &': {
              selectors: {
                ':focus': {
                  background: FOCUS_BACKGROUND_COLOR
                },
                ':focus:hover': {
                  background: HOVER_BACKGROUND_COLOR
                }
              }
            }
          }
        }
      ],
      dropdownItemSelected: {
        background: FOCUS_BACKGROUND_COLOR,
        color: HOVER_COLOR,
        selectors: {
          ':hover': {
            color: HOVER_COLOR
          },
          ':focus::after': {
            display: 'none'
          }
        }
      }
    } as Partial<IDropdownStyles>;

    return styles;
  };
}
